import * as React from 'react';
import { Select } from "antd";
import { Contract, TreeData } from "tree-wallet-client";
import { FormInstance } from "antd/lib/form";

const { Option } = Select;

interface TreeTypeSelectProps {
  treeData: any;
  contract: Contract;
  name: string;
  formRef: React.RefObject<FormInstance>;
  formSetState: Function;
}

const defaultLanguage = 'spanish';
const fallbackLanguage = 'latin';

interface OptionsThumbState {
  top: number;
  height: number;
}

// The mobile keyboard needs ~300ms to smoothly disappear, so the search input is blurred back down
// once that is over - the dropdown's own close animation is shorter than this, so it is never cut.
const KEYBOARD_DISPLAY_DELAY = 350;

// Scroll container for the options. antd only renders its own (always visible)
// scrollbar together with the virtual list, and iOS hides overlay scrollbars, so
// the position inside the list is drawn on top of the container instead.
function SelectOptionsScroll({ children }: { children?: React.ReactNode }) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = React.useState<OptionsThumbState | null>(null);

  const updateThumb = React.useCallback(() => {
    const el = scrollRef.current;
    let next: OptionsThumbState | null = null;

    if (el && el.scrollHeight > el.clientHeight) {
      const height = Math.max(24, (el.clientHeight / el.scrollHeight) * el.clientHeight);
      const top = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * (el.clientHeight - height);
      next = { top, height };
    }

    // Keep the previous object when nothing moved, otherwise setting state here
    // would re-render the component on every layout effect.
    setThumb((previous: OptionsThumbState | null) => (
      previous && next && previous.top === next.top && previous.height === next.height
        ? previous
        : next
    ));
  }, []);

  // The options change while searching, so the thumb is recomputed after each render.
  React.useLayoutEffect(updateThumb);

  return <div className={'select-options-wrapper'}>
    <div className={'select-options-scroll'} ref={scrollRef} onScroll={updateThumb}>
      {children}
    </div>
    {thumb &&
      <div className={'select-options-thumb'} style={{ top: thumb.top, height: thumb.height }} />
    }
  </div>;
}

export default function TreeTypeSelect(props: TreeTypeSelectProps) {
  const { treeData, contract, formRef, name, formSetState } = props;

  const filterOption = (input: string, option: any) => {
    if (!option?.value) {
      return false;
    }
    try {
      const treeIdx = parseInt(option?.value.toString());
      const translations = treeData && treeData[treeIdx].translations;
      const language = translations[contract.preferences.language]
        ? translations[contract.preferences.language]
        : translations[defaultLanguage];
      if (!language) {
        return translations[fallbackLanguage]?.toLowerCase().indexOf(input.toLowerCase()) > -1;
      }
      return language.toLowerCase().indexOf(input.toLowerCase()) > -1
        || translations[fallbackLanguage].toLowerCase().indexOf(input.toLowerCase()) > -1;
    } catch (err) {
      return false;
    }
  }

  const sortOptions = (treeData: TreeData[]) => {
    return treeData?.sort((a: TreeData, b: TreeData) => {
      try {
        const language = a.translations[contract.preferences.language]
          ? contract.preferences.language
          : fallbackLanguage;

        return a.translations[language].toLowerCase() > b.translations[language].toLowerCase() ? 1 : -1;
      } catch (err) {
        console.error(err, [a, b]);
        return a.translations[fallbackLanguage].toLowerCase() > b.translations[fallbackLanguage].toLowerCase() ? 1 : -1;
      }
    }).map((data: TreeData, idx: number) => {
      try {
        const language = data.translations[contract.preferences.language]
          ? contract.preferences.language
          : fallbackLanguage;
        return <Option value={idx} translations={data.translations} key={idx + '_' + language}>
          <span style={{
            fontWeight: '450',
            textTransform: 'capitalize'
          }}>
            {data.translations[language]}
          </span>
          <span style={{
            marginLeft: '10px',
            fontStyle: 'italic',
            textTransform: 'capitalize'
          }}>
            ({data.translations[fallbackLanguage]})
          </span>
        </Option>
      } catch (err) {
        console.error(err, { data, idx });
        return <Option value={idx} translations={data.translations} key={`${idx}_${fallbackLanguage}`}>
          {data.translations[fallbackLanguage]}
        </Option>
      }
    })
  };

  // Picking an option leaves the mobile keyboard open - with `showSearch` the Select keeps its search
  // input focused - so the input is blurred once the dropdown has closed and the keyboard has had the
  // time to slide away (see `KEYBOARD_DISPLAY_DELAY`).
  const closeKeyboardAfterDropdownCloses = () => {
    window.setTimeout(() => {
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur();
      }
    }, KEYBOARD_DISPLAY_DELAY);
  };

  // The wrapper is what keeps the option list in the page flow instead of on `body` (see
  // `getPopupContainer` below): it is the `parentElement` the dropdown is rendered into.
  return <div>
    <Select
      loading={treeData === undefined}
      showSearch
      size={'large'}
      // The virtual list (rc-virtual-list) does not scroll reliably on iOS: the
      // page scrolls instead of the options. Render the options natively and let
      // `.select-options-scroll` handle the scrolling.
      virtual={false}
      // Height of the option list: 5 options of 32px (`@select-dropdown-height`).
      // Keep in sync with `max-height` of `.select-options-scroll` in main.less.
      listHeight={160}
      placeholder={treeData === undefined ? "Loading..." : "Select a tree"}
      optionFilterProp="children"
      filterOption={filterOption}
      onChange={(value) => {
        formRef.current && formRef.current.setFieldsValue({ [name]: value });
        formSetState({ [name]: value });
        closeKeyboardAfterDropdownCloses();
      }}
      getPopupContainer={(triggerNode) => triggerNode.parentElement}
      dropdownRender={(menu: React.ReactElement) => <SelectOptionsScroll>{menu}</SelectOptionsScroll>}
    >
      {sortOptions(treeData)}
    </Select>
  </div>
}